/**
 * Affiliate apply — authenticated server functions.
 *
 * Split into two RPCs:
 *
 *   • getAffiliateApplyContext — form-loader gate. Returns the verified
 *     phone the UI should display read-only PLUS the current active
 *     terms snapshot (version + hash + rendered HTML) so the UI can
 *     display and enforce the scroll-gate. Refuses without a fresh
 *     WhatsApp-verified session.
 *
 *   • submitAffiliateApplication — submit gate. Ignores any body-supplied
 *     phone, version, hash, or timestamps. The server resolves the ACTIVE
 *     Affiliate Agreement snapshot itself and only accepts if all three
 *     explicit consent flags are true. On success it writes the application
 *     AND an immutable acceptance receipt via a service-role RPC.
 *
 * The old public route at /api/public/affiliate/apply was deleted in A2;
 * that prefix bypasses auth on published sites and would defeat the gate.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  authorizeApplyAccess,
  authorizeApplySubmit,
  type ApplyClaims,
} from "@/lib/affiliate/apply-gate";
import { normalizeApplication } from "@/lib/affiliate/validation";
// terms.server helpers are loaded via dynamic import inside handlers to keep
// node:crypto out of the client bundle graph.
import type * as TermsServer from "@/lib/affiliate/terms.server";
const loadTermsServer = () =>
  import("@/lib/affiliate/terms.server") as Promise<typeof TermsServer>;

// ---------------------------------------------------------------------------
// The exact user-visible consent labels. If a label ever changes, that is a
// material change and forces a new terms version and re-consent.
// ---------------------------------------------------------------------------
export const CONSENT_LABELS = {
  agreement:
    "I have read the TruWe Affiliate Partner Terms & Conditions. I understand that the binding Affiliate Agreement will be executed physically (signed on paper) after my application is reviewed and approved.",
  privacy:
    "I consent to TruWe processing my personal data (contact, application, KYC, payout, fraud-prevention, and compliance) as described in the Privacy Policy.",
  asci:
    "I understand and will follow the ASCI Guidelines for Influencer Advertising in Digital Media, and will use acceptable disclosure labels (e.g. #Ad, #Sponsored, #Collab, #Partnership) on every promotional post.",
} as const;

// ---------------------------------------------------------------------------

export type ApplyContext =
  | {
      ok: true;
      verifiedPhone: string;
      terms: {
        id: string;
        version: string;
        content_sha256: string;
        effective_at: string;
        content_html: string;
      };
    }
  | { ok: false; reason: "unverified" | "expired" | "invalid_phone" | "no_active_terms" };

export const getAffiliateApplyContext = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<ApplyContext> => {
    const gate = authorizeApplyAccess(context.claims as ApplyClaims);
    if (!gate.ok) return { ok: false, reason: gate.reason };

    // Fetch active agreement snapshot — RLS restricts authenticated to active rows.
    const { data: terms, error } = await context.supabase
      .from("affiliate_terms_versions")
      .select("id, version, content_sha256, effective_at, content_html")
      .eq("status", "active")
      .order("activated_at", { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle();

    if (error || !terms) return { ok: false, reason: "no_active_terms" };

    return {
      ok: true,
      verifiedPhone: gate.verifiedPhone,
      terms: {
        id: terms.id,
        version: terms.version,
        content_sha256: terms.content_sha256,
        effective_at: terms.effective_at as string,
        content_html: terms.content_html,
      },
    };
  });

// ---------------------------------------------------------------------------

export type SubmitResult =
  | { ok: true; status: "received"; receipt_number: string; receipt_url: string }
  | {
      ok: false;
      reason:
        | "unverified"
        | "expired"
        | "invalid_phone"
        | "phone_mismatch"
        | "invalid_input"
        | "duplicate"
        | "consent_incomplete"
        | "terms_unavailable";
    };

/**
 * All negative outcomes collapse into a generic shape; no enumeration.
 *
 * Consent rules (server-enforced, client-untrusted):
 *   • The client sends only three booleans and (optionally) a user-agent hash.
 *   • The server resolves the active version + hash from the DB.
 *   • The server refuses if any of the three flags is not === true.
 *   • The server refuses if there is no active version at all.
 *   • The server passes the server-resolved version/hash to the insert-RPC,
 *     which independently validates it before writing an immutable receipt.
 */
export const submitAffiliateApplication = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => {
    // Accept the same wide shape as the old public route so the client can
    // reuse its existing payload builder — normalization/validation happens
    // in normalizeApplication().
    return z.record(z.string(), z.unknown()).parse(raw ?? {});
  })
  .handler(async ({ data, context }): Promise<SubmitResult> => {
    const { sha256Hex, phoneFingerprint, maskIndianPhone, ipFingerprint } =
      await loadTermsServer();
    const body = data as Record<string, unknown>;
    const decision = authorizeApplySubmit(
      context.claims as ApplyClaims,
      body.phone,
    );
    if (!decision.ok) return { ok: false, reason: decision.reason };
    if (!context.userId) return { ok: false, reason: "unverified" };
    const authUserId: string = context.userId;

    // Consents: the ONLY client-supplied consent surface. Everything else
    // (version, hash, timestamps) is server-derived.
    if (body.consent_agreement !== true || body.consent_privacy !== true || body.consent_asci !== true) {
      return { ok: false, reason: "consent_incomplete" };
    }

    // Server-derived phone is authoritative; overwrite whatever came in.
    const withServerPhone = {
      ...body,
      phone: decision.verifiedPhone,
      // Force these true so normalizeApplication's legacy consent asserts pass.
      // The real consent enforcement is the guard above.
      consent_terms: true,
      consent_privacy: true,
      consent_asci: true,
    };

    let normalized;
    try {
      normalized = normalizeApplication(withServerPhone as never);
    } catch {
      return { ok: false, reason: "invalid_input" };
    }

    if (normalized.phone_normalized !== decision.verifiedPhone) {
      return { ok: false, reason: "phone_mismatch" };
    }

    // Server resolves ACTIVE agreement snapshot — client sends nothing here.
    const { data: activeTerms } = await context.supabase
      .from("affiliate_terms_versions")
      .select("id, version, content_sha256")
      .eq("status", "active")
      .order("activated_at", { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle();
    if (!activeTerms) return { ok: false, reason: "terms_unavailable" };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Duplicate check.
    const { data: existing } = await supabaseAdmin
      .from("affiliate_applications")
      .select("id")
      .or(
        `email_normalized.eq.${normalized.email_normalized},phone_normalized.eq.${normalized.phone_normalized}`,
      )
      .in("status", ["pending", "approved", "paused"])
      .limit(1)
      .maybeSingle();
    if (existing) return { ok: false, reason: "duplicate" };

    // Persist application. Consent version columns record the ACTIVE version at
    // submit time (server-derived; body's fields ignored).
    const { data: inserted, error: insertErr } = await supabaseAdmin
      .from("affiliate_applications")
      .insert({
        full_name: normalized.full_name,
        email_normalized: normalized.email_normalized,
        phone_normalized: decision.verifiedPhone,
        address_line: normalized.address_line,
        city: normalized.city,
        state: normalized.state,
        pincode: normalized.pincode,
        creator_type: normalized.creator_type,
        primary_social_url: normalized.primary_social_url,
        additional_socials: normalized.additional_socials,
        niche: normalized.niche,
        follower_range: normalized.follower_range,
        follower_count: normalized.follower_count,
        audience_geography: normalized.audience_geography,
        promotion_plan: normalized.promotion_plan,
        preferred_code: normalized.preferred_code,
        consent_terms: true,
        consent_privacy: true,
        consent_asci: true,
        consent_terms_version: activeTerms.version,
        consent_privacy_version: activeTerms.version,
        consent_asci_version: activeTerms.version,
        consent_terms_at: new Date().toISOString(),
        consent_privacy_at: new Date().toISOString(),
        consent_asci_at: new Date().toISOString(),
        auth_user_id: context.userId,
      })
      .select("id")
      .single();
    if (insertErr || !inserted) return { ok: false, reason: "duplicate" };

    // Insert immutable acceptance receipt via SECURITY DEFINER RPC.
    // The RPC re-validates version + hash server-side before writing.
    const uaRaw = typeof body.user_agent === "string" ? body.user_agent : null;
    const uaHash = uaRaw ? await sha256Hex(uaRaw.slice(0, 512)) : null;
    const phoneFp = await phoneFingerprint(decision.verifiedPhone);
    const ipFp = await ipFingerprint(
      typeof body.client_ip === "string" ? body.client_ip : null,
    );

    let receiptNumber: string | null = null;
    try {
      const rpcArgs = {
        _application_id: inserted.id,
        _auth_user_id: authUserId,
        _presented_version: activeTerms.version,
        _presented_sha256: activeTerms.content_sha256,
        _phone_fingerprint: phoneFp,
        _phone_masked: maskIndianPhone(decision.verifiedPhone),
        _consent_labels: CONSENT_LABELS,
        _locale: "en-IN",
        _user_agent_hash: uaHash,
        _ip_fingerprint: ipFp,
        _correlation_id:
          typeof body.correlation_id === "string" ? body.correlation_id.slice(0, 64) : null,
      } as unknown as Parameters<typeof supabaseAdmin.rpc<"affiliate_insert_acceptance">>[1];
      const { data: rpcData, error: rpcErr } = await supabaseAdmin.rpc(
        "affiliate_insert_acceptance",
        rpcArgs,
      );
      if (rpcErr) throw rpcErr;
      receiptNumber = (rpcData as Array<{ receipt_number: string }> | null)?.[0]?.receipt_number ?? null;
    } catch {
      // Roll application back so we don't leave a consent-less record.
      await supabaseAdmin.from("affiliate_applications").delete().eq("id", inserted.id);
      return { ok: false, reason: "terms_unavailable" };
    }

    if (!receiptNumber) {
      await supabaseAdmin.from("affiliate_applications").delete().eq("id", inserted.id);
      return { ok: false, reason: "terms_unavailable" };
    }

    // Queue exactly-once transactional receipt email. Failure to send does
    // NOT roll back the receipt — the receipt itself is the primary proof;
    // email is a convenience copy. Delivery status is stored in
    // affiliate_receipt_email_deliveries for later retry.
    try {
      // Look up the newly-inserted receipt id so we can key the delivery row.
      const { data: recRow } = await supabaseAdmin
        .from("affiliate_acceptance_receipts")
        .select("id")
        .eq("receipt_number", receiptNumber)
        .maybeSingle();
      if (recRow?.id) {
        const { queueAffiliateReceiptEmail } = await import(
          "@/lib/affiliate/receipt.functions"
        );
        await queueAffiliateReceiptEmail({
          receiptId: recRow.id,
          receiptNumber,
          recipientEmail: normalized.email_normalized,
          fullName: normalized.full_name,
          termsVersion: activeTerms.version,
          termsSha256: activeTerms.content_sha256,
          acceptedAtIso: new Date().toISOString(),
          phoneMasked: maskIndianPhone(decision.verifiedPhone),
        });
      }
    } catch {
      // swallow — receipt is written; email will be retried by a future job.
    }

    return {
      ok: true,
      status: "received",
      receipt_number: receiptNumber,
      receipt_url: `/affiliate/receipt?n=${encodeURIComponent(receiptNumber)}`,
    };
  });
